import {
  ChannelUnsubscribe,
  channelClient,
  ContractShapeOf,
  RequestPolicies,
  ContractEvent,
  ContractEventOf,
} from 'shared/channels';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { createUniqueId } from 'shared/utils/id/id';
import type {
  DirectoriesBatchCreatedModel,
  DirectoriesBatchCreateModel,
  DirectoryCreateModel,
  DirectoryEditModel,
  DirectoryModel,
} from './directoryModel';
import { BreadcrumbItem, directoryContract, UploadedDirectoryPayload } from './directoryContract';

/** Directories as served by the service worker. */
export const directoryClient = {
  getChildren(parentId: string): Promise<DirectoryModel[]> {
    return channelClient.request(directoryContract, 'getChildren', { parentId }, RequestPolicies.read);
  },

  getAll(): Promise<DirectoryModel[]> {
    return channelClient.request(directoryContract, 'getAll', undefined, RequestPolicies.bulkRead);
  },

  /** From the root down to `directoryId`; empty for the root itself. */
  async getBreadcrumbs(directoryId: string): Promise<BreadcrumbItem[]> {
    if (!directoryId) {
      return [];
    }
    return channelClient.request(directoryContract, 'getBreadcrumbs', { directoryId }, RequestPolicies.read);
  },

  /** Resolves with the uploaded directory once the server created it. */
  create(directory: DirectoryCreateModel): Promise<UploadedDirectoryPayload> {
    const uniqueId = createUniqueId();
    return channelClient.command(
      directoryContract,
      'create',
      { directory, uniqueId },
      { type: DomainMessagesTypes.uploadedEntity, correlationId: uniqueId },
      RequestPolicies.mutation
    );
  },

  /** Resolves once the server created every directory of the batch. */
  createBatch(directories: DirectoriesBatchCreateModel): Promise<DirectoriesBatchCreatedModel> {
    const uniqueId = createUniqueId();
    return channelClient.command(
      directoryContract,
      'createBatch',
      { directories, uniqueId },
      { type: DomainMessagesTypes.directoriesBatchCreated, correlationId: uniqueId },
      // The worker posts the whole tree to the API in one request, which takes long for big trees.
      RequestPolicies.longRunning
    );
  },

  /** Resolves once the edit is saved locally (the upload follows). */
  async edit(directory: DirectoryEditModel, uniqueId: string = createUniqueId()): Promise<void> {
    await channelClient.command(
      directoryContract,
      'edit',
      { directory, uniqueId },
      { type: DomainMessagesTypes.updatedEntity, correlationId: uniqueId },
      RequestPolicies.mutation
    );
  },

  sync(): void {
    channelClient.notify(directoryContract, 'sync', undefined);
  },

  /** Every directory event; `switch (event.type)` narrows `event.data`. */
  onEvent(
    handler: (event: ContractEvent<ContractShapeOf<typeof directoryContract>>) => void
  ): ChannelUnsubscribe {
    return channelClient.onEvent(directoryContract, handler);
  },

  /** Directories the server created, from this tab or another one. */
  onUploaded(
    handler: (event: ContractEventOf<typeof directoryContract, DomainMessagesTypes.uploadedEntity>) => void
  ): ChannelUnsubscribe {
    return channelClient.onEventOfType(directoryContract, DomainMessagesTypes.uploadedEntity, handler);
  },
};
